import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { patientApi, medicationApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Pill, AlertTriangle, Send, Search, CheckCircle, XCircle,
  Plus, ChevronLeft, Building2
} from 'lucide-react';

export default function EPrescribePage() {
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [medications, setMedications] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRx, setShowNewRx] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [checking, setChecking] = useState(false);
  
  const [newRx, setNewRx] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    quantity: 30,
    refills: 0,
    route: 'oral',
    instructions: '',
    pharmacy_id: ''
  });

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [patientRes, medsRes, rxRes, pharmRes] = await Promise.all([
        patientId ? patientApi.get(patientId) : Promise.resolve({ data: null }),
        patientId ? medicationApi.list(patientId, false) : Promise.resolve({ data: [] }),
        api.get('/prescriptions', { params: { patient_id: patientId } }),
        api.get('/pharmacies')
      ]);
      
      setPatient(patientRes.data);
      setMedications(medsRes.data || []);
      setPrescriptions(rxRes.data.prescriptions || []);
      setPharmacies(pharmRes.data.pharmacies || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkInteractions = async (medName) => {
    if (!medName || !patientId) return;
    
    setChecking(true);
    try {
      const response = await api.post('/prescriptions/check-interactions', {
        patient_id: patientId,
        new_medication: medName
      });
      setInteractions(response.data.interactions || []);
    } catch (error) {
      console.error('Interaction check failed:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleCreatePrescription = async () => {
    if (!newRx.medication_name || !newRx.dosage || !newRx.frequency) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.post('/prescriptions', {
        patient_id: patientId,
        ...newRx
      });
      
      if (response.data.interactions?.length > 0) {
        toast.warning('Drug interactions detected. Please review before sending.');
      } else {
        toast.success('Prescription created');
      }
      
      setShowNewRx(false);
      setNewRx({ medication_name: '', dosage: '', frequency: '', quantity: 30, refills: 0, route: 'oral', instructions: '', pharmacy_id: '' });
      setInteractions([]);
      loadData();
    } catch (error) {
      toast.error('Failed to create prescription');
    }
  };

  const handleSendPrescription = async (rxId) => {
    try {
      await api.post(`/prescriptions/${rxId}/send`);
      toast.success('Prescription sent to pharmacy');
      loadData();
    } catch (error) {
      toast.error('Failed to send prescription');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="eprescribe-page">
      {/* Header */}
      {patientId && (
        <Link to={`/patients/${patientId}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0A2342]">
          <ChevronLeft className="w-4 h-4" />
          Back to Patient
        </Link>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            E-Prescribing
          </h1>
          {patient && (
            <p className="text-[#64748B] mt-1">
              Patient: {patient.first_name} {patient.last_name} (MRN: {patient.mrn})
            </p>
          )}
        </div>
        <Dialog open={showNewRx} onOpenChange={setShowNewRx}>
          <DialogTrigger asChild>
            <Button className="careiq-btn-primary flex items-center gap-2" data-testid="new-prescription-btn">
              <Plus className="w-4 h-4" />
              New Prescription
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Prescription</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Medication Name with Interaction Check */}
              <div>
                <Label className="careiq-label">Medication Name *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter medication name"
                    value={newRx.medication_name}
                    onChange={(e) => setNewRx({ ...newRx, medication_name: e.target.value })}
                    onBlur={() => checkInteractions(newRx.medication_name)}
                    className="careiq-input"
                    data-testid="rx-medication-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => checkInteractions(newRx.medication_name)}
                    disabled={checking}
                  >
                    {checking ? <div className="w-4 h-4 border-2 border-[#0E7490] border-t-transparent rounded-full animate-spin" /> : 'Check'}
                  </Button>
                </div>
              </div>

              {/* Interaction Warning */}
              {interactions.length > 0 && (
                <div className="p-4 bg-[#991B1B]/10 border border-[#991B1B] rounded-lg">
                  <div className="flex items-center gap-2 text-[#991B1B] font-medium mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    Drug Interactions Detected
                  </div>
                  <ul className="space-y-2">
                    {interactions.map((inter, idx) => (
                      <li key={idx} className="text-sm text-[#991B1B]">
                        <strong>{inter.interaction_type}:</strong> {inter.recommendation}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="careiq-label">Dosage *</Label>
                  <Input
                    placeholder="e.g., 500mg"
                    value={newRx.dosage}
                    onChange={(e) => setNewRx({ ...newRx, dosage: e.target.value })}
                    className="careiq-input"
                  />
                </div>
                <div>
                  <Label className="careiq-label">Frequency *</Label>
                  <Select value={newRx.frequency} onValueChange={(v) => setNewRx({ ...newRx, frequency: v })}>
                    <SelectTrigger className="careiq-input">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once daily">Once daily</SelectItem>
                      <SelectItem value="twice daily">Twice daily</SelectItem>
                      <SelectItem value="three times daily">Three times daily</SelectItem>
                      <SelectItem value="four times daily">Four times daily</SelectItem>
                      <SelectItem value="every 4 hours">Every 4 hours</SelectItem>
                      <SelectItem value="every 6 hours">Every 6 hours</SelectItem>
                      <SelectItem value="every 8 hours">Every 8 hours</SelectItem>
                      <SelectItem value="as needed">As needed (PRN)</SelectItem>
                      <SelectItem value="at bedtime">At bedtime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="careiq-label">Quantity</Label>
                  <Input
                    type="number"
                    value={newRx.quantity}
                    onChange={(e) => setNewRx({ ...newRx, quantity: parseInt(e.target.value) })}
                    className="careiq-input"
                  />
                </div>
                <div>
                  <Label className="careiq-label">Refills</Label>
                  <Input
                    type="number"
                    min="0"
                    max="12"
                    value={newRx.refills}
                    onChange={(e) => setNewRx({ ...newRx, refills: parseInt(e.target.value) })}
                    className="careiq-input"
                  />
                </div>
                <div>
                  <Label className="careiq-label">Route</Label>
                  <Select value={newRx.route} onValueChange={(v) => setNewRx({ ...newRx, route: v })}>
                    <SelectTrigger className="careiq-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oral">Oral</SelectItem>
                      <SelectItem value="topical">Topical</SelectItem>
                      <SelectItem value="injection">Injection</SelectItem>
                      <SelectItem value="inhalation">Inhalation</SelectItem>
                      <SelectItem value="sublingual">Sublingual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="careiq-label">Instructions</Label>
                <Textarea
                  placeholder="Special instructions for patient..."
                  value={newRx.instructions}
                  onChange={(e) => setNewRx({ ...newRx, instructions: e.target.value })}
                  className="careiq-input"
                />
              </div>

              <div>
                <Label className="careiq-label">Pharmacy</Label>
                <Select value={newRx.pharmacy_id} onValueChange={(v) => setNewRx({ ...newRx, pharmacy_id: v })}>
                  <SelectTrigger className="careiq-input">
                    <SelectValue placeholder="Select pharmacy" />
                  </SelectTrigger>
                  <SelectContent>
                    {pharmacies.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} - {p.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowNewRx(false)}>Cancel</Button>
                <Button className="careiq-btn-primary" onClick={handleCreatePrescription} data-testid="create-rx-btn">
                  Create Prescription
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Current Medications */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Pill className="w-5 h-5 text-[#0E7490]" />
              Current Medications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {medications.length > 0 ? (
              <div className="space-y-3">
                {medications.map((med) => (
                  <div key={med.id} className="p-3 rounded-lg bg-[#F8FAFC]">
                    <div className="font-medium text-[#0A2342]">{med.name}</div>
                    <div className="text-sm text-[#64748B]">{med.dosage} • {med.frequency}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#64748B] text-center py-4">No active medications</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Prescriptions */}
        <Card className="lg:col-span-2 careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Prescriptions</CardTitle>
          </CardHeader>
          <CardContent>
            {prescriptions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="table-header text-left">Medication</th>
                      <th className="table-header text-left">Dosage</th>
                      <th className="table-header text-left">Status</th>
                      <th className="table-header text-left">Date</th>
                      <th className="table-header text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescriptions.map((rx) => (
                      <tr key={rx.id} className="table-row">
                        <td className="table-cell font-medium">{rx.medication_name}</td>
                        <td className="table-cell">{rx.dosage} • {rx.frequency}</td>
                        <td className="table-cell">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            rx.status === 'sent' ? 'bg-[#166534]/10 text-[#166534]' :
                            rx.status === 'pending' ? 'bg-[#92400E]/10 text-[#92400E]' :
                            'bg-[#64748B]/10 text-[#64748B]'
                          }`}>
                            {rx.status}
                          </span>
                        </td>
                        <td className="table-cell text-[#64748B]">
                          {new Date(rx.created_at).toLocaleDateString()}
                        </td>
                        <td className="table-cell">
                          {rx.status === 'pending' && (
                            <Button size="sm" className="careiq-btn-secondary" onClick={() => handleSendPrescription(rx.id)}>
                              <Send className="w-3 h-3 mr-1" />
                              Send
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[#64748B] text-center py-8">No prescriptions on file</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
